import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import Register from './pages/Register';
import SetupPasscode from './pages/SetupPasscode';
import VerifyPasscode from './pages/VerifyPasscode';
import UserDashboard from './pages/UserDashboard';
import AdminDashboard from './pages/AdminDashboard';
import Transactions from './pages/Transactions';
import Profile from './pages/Profile';
import UserProfile from './pages/UserProfile';
import Banks from './pages/Banks';
import GuarantorVerify from './pages/GuarantorVerify';
import ApplicationProgress from './pages/ApplicationProgress';

import Home from './pages/Home';
import Services from './pages/Services';
import AboutUs from './pages/AboutUs';
import ContactUs from './pages/ContactUs';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';
import PayOnline from './pages/PayOnline';

const PrivateRoute = ({ children, adminOnly = false }) => {
  const { currentUser, isAdmin, userData, isPasscodeVerified } = useAuth();
  const location = useLocation();
  
  if (!currentUser) return <Navigate to="/login" />;

  // Admin access check for restricted routes
  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" />;
  }

  // PASSCODE FLOW (Skip for admins as requested)
  if (!isAdmin) {
    // Force passcode setup for users who don't have one
    if (!userData?.passcode && location.pathname !== '/setup-passcode') {
       return <Navigate to="/setup-passcode" />;
    }

    // If set up but NOT verified, force verification wall (Don't redirect if we are already on verify or setup pages)
    if (userData?.passcode && !isPasscodeVerified && location.pathname !== '/verify-passcode' && location.pathname !== '/setup-passcode') {
      return <Navigate to="/verify-passcode" />;
    }
  }
  
  return children;
};

const SetupPasscodeWrapper = () => {
    const { currentUser, userData } = useAuth();
    if (!currentUser) return <Navigate to="/login" />;
    if (userData?.passcode) return <Navigate to="/dashboard" />;
    return <SetupPasscode />;
}

const HomeNav = () => {
  const { isAdmin } = useAuth();
  return isAdmin ? <Navigate to="/admin" /> : <UserDashboard />;
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/guarantor-verify/:loanId" element={<GuarantorVerify />} />
          <Route path="/setup-passcode" element={<SetupPasscodeWrapper />} />
          
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsConditions />} />
          <Route path="/pay-online" element={<PayOnline />} />
          
          <Route path="/verify-passcode" element={
            <PrivateRoute>
              <VerifyPasscode />
            </PrivateRoute>
          } />
          <Route path="/dashboard" element={
            <PrivateRoute>
              <HomeNav />
            </PrivateRoute>
          } />

          <Route path="/admin/*" element={
            <PrivateRoute adminOnly={true}>
              <AdminDashboard />
            </PrivateRoute>
          } />

          <Route path="/transactions" element={
            <PrivateRoute>
              <Transactions />
            </PrivateRoute>
          } />

          <Route path="/banks" element={
            <PrivateRoute>
              <Banks />
            </PrivateRoute>
          } />

          <Route path="/profile" element={
            <PrivateRoute>
              <Profile />
            </PrivateRoute>
          } />

          <Route path="/application-progress/:loanId" element={
            <PrivateRoute>
              <ApplicationProgress />
            </PrivateRoute>
          } />

          <Route path="/user-profile" element={
            <PrivateRoute>
              <UserProfile />
            </PrivateRoute>
          } />

          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
